import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Search,
    CheckCircle2,
    Clock,
    AlertTriangle,
    Filter,
    Sparkles,
    RefreshCw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_BADGES = {
    new: { label: "New", cls: "bg-terracotta/15 text-terracotta" },
    "in-review": { label: "In review", cls: "bg-sage/25 text-moss" },
    completed: { label: "Completed", cls: "bg-seaGlass/40 text-moss" },
    archived: { label: "Archived", cls: "bg-sand text-foreground/65" },
};

export default function AdminDashboardPage() {
    const { authedFetch } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await authedFetch("/admin/intakes?limit=200");
            setItems(data.items || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load intakes.");
        } finally {
            setLoading(false);
        }
    }, [authedFetch]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((i) => {
            if (statusFilter !== "all" && i.status !== statusFilter) return false;
            if (!q) return true;
            return (
                (i.first_name || "").toLowerCase().includes(q) ||
                (i.last_name || "").toLowerCase().includes(q) ||
                (i.email || "").toLowerCase().includes(q) ||
                (i.primary_concern || "").toLowerCase().includes(q)
            );
        });
    }, [items, search, statusFilter]);

    const counts = useMemo(() => {
        const c = { all: items.length, new: 0, "in-review": 0, completed: 0, archived: 0 };
        items.forEach((i) => {
            c[i.status] = (c[i.status] || 0) + 1;
        });
        return c;
    }, [items]);

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-3xl text-moss">Client intakes</h1>
                    <p className="mt-1 text-sm text-foreground/65">
                        Each submission is auto-pushed to your GHL sub-account and pre-analyzed by Claude Sonnet 4.5.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-refresh"
                    className="inline-flex items-center gap-2 rounded-xl bg-sand px-4 py-2 text-sm text-moss hover:bg-sand/80"
                >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="rounded-2xl border border-border/80 bg-white/85 p-4 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
                    <input
                        type="search"
                        placeholder="Search by name, email, or concern..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="admin-search"
                        className="w-full rounded-xl bg-cream border border-border/80 pl-9 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                    />
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                    <Filter className="h-4 w-4 text-foreground/55 mr-1" />
                    {[
                        ["all", "All"],
                        ["new", "New"],
                        ["in-review", "In review"],
                        ["completed", "Completed"],
                        ["archived", "Archived"],
                    ].map(([val, label]) => (
                        <button
                            key={val}
                            type="button"
                            onClick={() => setStatusFilter(val)}
                            data-testid={`admin-filter-${val}`}
                            className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                                statusFilter === val
                                    ? "bg-moss text-cream border-moss"
                                    : "bg-white text-foreground/75 border-border/80 hover:border-moss/40"
                            }`}
                        >
                            {label}
                            <span className="ml-1.5 opacity-70">({counts[val] || 0})</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="mt-6 rounded-2xl border border-border/80 bg-white/90 overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-foreground/55">Loading intakes...</div>
                ) : error ? (
                    <div className="p-10 text-center text-terracotta">{error}</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-foreground/55">
                        No intake submissions match your filters yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table
                            data-testid="admin-intakes-table"
                            className="w-full text-sm"
                        >
                            <thead className="bg-sand/50 text-left text-xs uppercase tracking-wider text-foreground/65">
                                <tr>
                                    <th className="p-4">Client</th>
                                    <th className="p-4">Concern</th>
                                    <th className="p-4">Submitted</th>
                                    <th className="p-4">Status</th>
                                    <th className="p-4">AI</th>
                                    <th className="p-4">GHL</th>
                                    <th className="p-4"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/70">
                                {filtered.map((it) => {
                                    const sb =
                                        STATUS_BADGES[it.status] || STATUS_BADGES.new;
                                    const ghl = it.ghl || {};
                                    return (
                                        <tr
                                            key={it.id}
                                            data-testid="admin-intake-row"
                                            className="hover:bg-sand/30 transition-colors"
                                        >
                                            <td className="p-4">
                                                <Link
                                                    to={`/admin/intakes/${it.id}`}
                                                    className="block group"
                                                >
                                                    <p className="font-medium text-foreground/90 group-hover:text-terracotta">
                                                        {it.first_name} {it.last_name}
                                                    </p>
                                                    <p className="text-xs text-foreground/60">
                                                        {it.email}
                                                    </p>
                                                </Link>
                                            </td>
                                            <td className="p-4 max-w-xs">
                                                <p className="text-foreground/80 line-clamp-2">
                                                    {it.primary_concern || (
                                                        <span className="text-foreground/40">—</span>
                                                    )}
                                                </p>
                                            </td>
                                            <td className="p-4 text-foreground/65 text-xs">
                                                {new Date(it.submitted_at).toLocaleDateString()}
                                                <br />
                                                <span className="text-foreground/45">
                                                    {new Date(it.submitted_at).toLocaleTimeString()}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span
                                                    className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${sb.cls}`}
                                                >
                                                    {sb.label}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                {it.has_ai ? (
                                                    <span className="inline-flex items-center gap-1 text-xs text-moss">
                                                        <Sparkles className="h-3.5 w-3.5" /> ready
                                                    </span>
                                                ) : it.ai_error ? (
                                                    <span
                                                        title={it.ai_error}
                                                        className="inline-flex items-center gap-1 text-xs text-terracotta"
                                                    >
                                                        <AlertTriangle className="h-3.5 w-3.5" />
                                                        error
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs text-foreground/50">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                {ghl.status === "synced" ? (
                                                    <span className="inline-flex items-center gap-1 text-xs text-moss">
                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                        synced
                                                    </span>
                                                ) : ghl.status === "failed" ? (
                                                    <span className="inline-flex items-center gap-1 text-xs text-terracotta">
                                                        <AlertTriangle className="h-3.5 w-3.5" />
                                                        failed
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs text-foreground/50">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right">
                                                <Link
                                                    to={`/admin/intakes/${it.id}`}
                                                    className="inline-flex items-center text-sm font-medium text-moss hover:text-terracotta"
                                                >
                                                    Open →
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
